import { Filter } from "../../models/filter.model";

/**
 * Catálogo centralizado de definiciones de filtros para el componente filter-list.
 * 
 * Esta constante proporciona todas las configuraciones de filtros disponibles en la aplicación,
 * incluyendo filtros de búsqueda, estado, fechas, y opciones específicas de dominio.
 * 
 * Cada entrada representa un filtro con su configuración completa (key, label, opciones, tipo, etc.)
 * que puede ser utilizado por el componente filter-list para renderizar controles de filtrado.
 * 
 * @constant
 * @type {Record<string, Filter>}
 * 
 * @example
 * // Acceder a un filtro específico
 * const marketFilter = filterList['market_id'];
 * 
 * @example
 * // Usar en un componente
 * this.filters = [
 *   filterList['product_id'],
 *   filterList['order_type'],
 *   filterList['delivery_range']
 * ];
 **/
const filterList: Record<string, Filter> = {
  commercial_zone: new Filter({
    key: 'commercial_zone',
    label: 'FILTERS.COMMERCIAL_ZONE'
  }),

  market_id: new Filter({
    key: 'market_id',
    label: 'GLOBAL.MARKET',
    admin: true,
  }),

  product_id: new Filter({
    key: 'product_id',
    label: 'GLOBAL.PRODUCT'
  }),

  order_type: new Filter({
    key: 'order_type',
    label: 'FILTERS.ORDER_TYPE',
    options: [
      { id: 4, name: 'GLOBAL.AUCTION' },
      { id: 1, name: 'GLOBAL.ORDER' }
    ]
  }),

  operation_type: new Filter({
    key: 'operation_type',
    label: 'FILTERS.OPERATION_TYPE',
    options: [
      { id: 'compra', name: 'GLOBAL.BID' },
      { id: 'venta', name: 'GLOBAL.SELL' }
    ]
  }),

  business_type: new Filter({
    key: 'business_type',
    label: 'ORDER_FORM.BUSINESS_TYPE.LABEL',
    options: [
      { id: 'odd', name: 'ORDER_FORM.BUSINESS_TYPE.OPTIONS.odd' },
      { id: 'tradicional', name: 'ORDER_FORM.BUSINESS_TYPE.OPTIONS.tradicional' }
    ]
  }),

  zone_id: new Filter({
    key: 'zone_id',
    label: 'FILTERS.DELIVERY_ZONE'
  }),

  zones_id: new Filter({
    key: 'zones_id',
    label: 'FILTERS.DELIVERY_ZONES',
    type: 'multiple'
  }),

  envelope_status: new Filter({
    key: 'envelope_status',
    label: 'FILTERS.ENVELOPE_STATUS',
    options: [
      { id: 10, name: 'SIGNATURES.STATUS.NOT_SENT' },
      { id: 20, name: 'SIGNATURES.STATUS.PENDING' },
      { id: 30, name: 'SIGNATURES.STATUS.COMPLETED' },
      { id: 40, name: 'SIGNATURES.STATUS.CANCELED' }
    ]
  }),

  status_group_orders: new Filter({
    key: 'status_group_orders',
    label: 'GLOBAL.STATUS',
    options: [
      { id: '1', name: 'FILTERS.OPEN' },
      { id: '2', name: 'FILTERS.CLOSED' }
    ]
  }),

  status_barter_proposals: new Filter({
    key: 'status_barter_proposals', // name or key reference
    label: 'GLOBAL.STATUS',
    options: [
      { id: '1', name: 'FILTERS.OPEN_BARTER' },
      { id: '2', name: 'FILTERS.CLOSED_BARTER' }
    ]
  }),

  delivery_range: new Filter({
    key: 'delivery_range',
    label: 'FILTERS.DELIVERY_RANGE',
    type: 'date_range'
    // options: [
    //   { id: 'true', name: 'FILTERS.DELIVERY_RANGE' }
    // ],
  }),

  fixation_period: new Filter({
    key: 'fixation_period',
    label: 'FILTERS.FIXATION_PERIOD',
    type: 'date_range'
    // options: [
    //   { id: 'true', name: 'FILTERS.FIXATION_PERIOD' }
    // }]
  }),

  date_issued: new Filter({
    key: 'date_issued',
    label: 'FILTERS.ASSIGNMENT_RANGE',
    type: 'date_range'
    // options: [{
    //     'id': 'true',
    //     'name': 'FILTERS.ASSIGNMENT_RANGE'
    // }]
  }),

  payment_condition: new Filter({
    key: 'payment_condition',
    label: 'GLOBAL.PAYMENT_CONDITION',
    options: [
      { id: 1, name: 'FILTERS.CASH', iconHtml: '<i class="image-icons cash-payment"></i>', class: 'highlighted' }
    ]
  }),

  validity: new Filter({
    key: 'validity',
    label: 'FILTERS.VALIDITY',
    options: [
      // {
      //   'id': 'on_date',
      //   'name': 'FILTERS.ON_DATE'
      // },
      { id: 'valid', name: 'FILTERS.VALID' }, //vigente
      { id: 'archived', name: 'FILTERS.ARCHIVED' }
    ]
  }),

  price: new Filter({
    key: 'price',
    label: 'FILTERS.PRICE',
  }),

  fintech_status: new Filter({
    key: 'fintech_status',
    label: 'GLOBAL.STATUS',
    options: [
      { id: 'active', name: 'FILTERS.FINTECH.ACTIVE' },
      { id: 'granted', name: 'FILTERS.FINTECH.GRANTED' },
      { id: 'rejected', name: 'FILTERS.FINTECH.REJECTED' },
      { id: 'withdrawed', name: 'FILTERS.FINTECH.WITHDRAWN' },
      { id: 'expired', name: 'FILTERS.FINTECH.EXPIRED' }
    ]
  }),

  status: new Filter({
    key: 'price',
    label: 'GLOBAL.STATUS',
    options: [
      { id: 0, name: 'FILTERS.FINTECH.ACTIVE' },
      { id: 1, name: 'FILTERS.FINTECH.REJECTED' },
      { id: 2, name: 'FILTERS.FINTECH.GRANTED' },
      // {id: 3, name: 'FILTERS.FINTECH.WITHDRAWN' },
      { id: 4, name: 'FILTERS.FINTECH.EXPIRED' },
      { id: 6, name: 'FILTERS.FINTECH.WITHDRAWN' }
    ]
  }),

  has_contract: new Filter({
    key: 'has_contract',
    label: 'GLOBAL.CONTRACT',
    options: [
      { id: 'true', name: 'FILTERS.WITH_CONTRACT' },
      { id: 'false', name: 'FILTERS.WITHOUT_CONTRACT' }
    ]
  }),

  active: new Filter({
    key: 'active',
    label: 'GLOBAL.STATUS',
    admin: true,
    options: [
      { id: 'true', name: 'FILTERS.ACTIVE' },
      { id: 'false', name: 'FILTERS.INACTIVE' }
    ]
  }),

  label_id: new Filter({
    key: 'label_id',
    label: "FILTERS.LABELS",
  }),

  activity_id: new Filter({
    key: 'activity_id',
    label: 'GLOBAL.MAIN_ACTIVITY',
  }),

  sustainable: new Filter({
    key: 'sustainable',
    label: 'ORDER.PRODUCTION',
    options: [
      {
        id: 'true', name: 'ORDER_FORM.PRODUCT.SUSTAINABLE', iconHtml: '<i class="glyphicon glyphicon-leaf"></i>', class: 'highlighted'
      }
    ]
  }),

  differences: new Filter({
    key: 'differences',
    label: 'CONTRACTS.DIFF.TITLE',
    options: [
      { id: 'true', name: 'CONTRACTS.DIFF.YES', iconHtml: '<span class="material-symbols-rounded">swap_horizontal_circle</span>', class: 'warning' },
      { id: 'false', name: 'CONTRACTS.DIFF.NO' }
    ]
  }),

  warnings: new Filter({
    key: 'warnings',
    label: 'IMPORTER.WARNING.TITLE',
    options: [
      { id: 'true', name: 'IMPORTER.WARNING.YES', iconHtml: '<span class="material-symbols-rounded">warning</span>', class: 'warning' },
      { id: 'false', name: 'IMPORTER.WARNING.NO' }
    ]
  }),

  created_at: new Filter({
    key: 'created_at',
    label: 'GLOBAL.DATE',
    type: 'date_range'
  }),

  delivery_type: new Filter({
    key: 'delivery_type',
    label: 'GLOBAL.DELIVERY_TYPE'
  }),

  range: new Filter({
    key: 'range',
    label: 'GLOBAL.PERIOD',
  }),

  past_range: new Filter({
    key: 'past_range',
    label: 'GLOBAL.PERIOD',
  }),

  crop: new Filter({
    key: 'crop',
    label: 'GLOBAL.CROP_YEAR',
  }),

  modality: new Filter({
    key: 'modality',
    label: 'CONTRACT.MODALITY.LABEL',
    options: [
      { id: 'avc', name: 'CONTRACT.MODALITY.AVC' },
      { id: 'btb', name: 'CONTRACT.MODALITY.BTB' }
    ]
  }),

  page: new Filter({
    key: 'page',
    value: 1
  }),

  unassigned: new Filter({
    key: 'unassigned',
    label: 'GLOBAL.STATE',
    options: [
      { id: 'false', name: 'SLOTS.TABLE_HEADER.ASSIGNED_SLOTS' },
      { id: 'true', name: 'SLOTS.TABLE_HEADER.PENDING_SLOTS' }
    ]
  }),

  slot_request_status: new Filter({
    key: 'status_id',
    label: 'GLOBAL.STATUS',
    options: [
      { id: 1, name: 'SLOTS.STATUS.PENDING' },
      { id: 2, name: 'SLOTS.STATUS.EXPIRED' },
      { id: 3, name: 'SLOTS.STATUS.ASSIGNED' },
      { id: 4, name: 'SLOTS.STATUS.ASSIGNED_PARTIALLY' },
      { id: 5, name: 'SLOTS.STATUS.WITHDRAWN' },
      { id: 6, name: 'SLOTS.STATUS.REJECTED' }
    ]
  }),

  slot_request_active: new Filter({
    key: 'active',
    label: 'GLOBAL.STATUS',
    // admin: true,
    options: [
      { id: 'true', name: 'FILTERS.ACTIVE' },
      // { id: 'false', name: 'FILTERS.INACTIVE' }
    ]
  }),

  slot_request_date: new Filter({
    key: 'slot_request_date',
    label: 'FILTERS.ASSIGNMENT_RANGE',
    type: 'date_range'
  }),
};

export default filterList;
